import { useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { Marca } from './Marca.js';
import { Botao } from './ui/index.js';

const classeCampo =
  'w-full rounded-(--radius-controle) border border-borda-forte bg-superficie px-3 py-2 text-base outline-none focus:border-destaque';

/**
 * Entrada no sistema.
 *
 * Senha, e nao link magico por e-mail: o link magico exige abrir o e-mail, e no
 * haras nao ha rede para isso. A equipe entra uma vez no escritorio e a sessao
 * dura a viagem inteira.
 */
export function Login() {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [recuperar, setRecuperar] = useState(false);
  const [enviado, setEnviado] = useState(false);

  /*
    "Esqueci a senha": o Supabase manda um link por e-mail; ao voltar por ele, o
    app pede a senha nova (NovaSenha). A resposta e a mesma exista ou nao o
    e-mail — ninguem descobre por aqui quem tem conta.
  */
  const pedirLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
    setEnviando(false);
    if (error && !navigator.onLine) setErro('Sem conexão. Tente de novo quando houver internet.');
    else setEnviado(true);
  };

  if (recuperar) {
    return (
      <main className="flex min-h-dvh items-center justify-center p-6">
        <form onSubmit={pedirLink} className="w-full max-w-sm space-y-6">
          <header className="space-y-4">
            <Marca tom="tema" />
            <div className="space-y-1">
              <h1 className="font-serif text-3xl font-semibold text-marca">Esqueci a senha</h1>
              <p className="text-sm text-apagado">Enviamos um link para você criar uma senha nova.</p>
            </div>
          </header>
          {enviado ? (
            <p role="status" className="border-l-2 border-ok px-3 py-2 text-sm">
              Se este e-mail estiver cadastrado, o link chega em alguns minutos. Confira também a caixa de spam.
            </p>
          ) : (
            <label className="block space-y-1.5">
              <span className="text-sm font-medium">E-mail</span>
              <input type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={classeCampo} />
            </label>
          )}
          {erro && (
            <p role="alert" className="border-l-2 border-erro bg-erro/8 px-3 py-2 text-sm text-erro">
              {erro}
            </p>
          )}
          {!enviado && (
            <Botao type="submit" variante="principal" carregando={enviando} className="h-11 w-full">
              Enviar o link
            </Botao>
          )}
          <button type="button" onClick={() => setRecuperar(false)} className="text-sm text-destaque-texto hover:underline">
            Voltar para entrar
          </button>
        </form>
      </main>
    );
  }

  const entrar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password: senha });
    if (error) {
      setErro(
        navigator.onLine
          ? 'E-mail ou senha incorretos.'
          : 'Sem conexão. O primeiro acesso precisa ser feito com internet.',
      );
    }
    setEnviando(false);
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,5fr)_minmax(0,4fr)]">
      {/* Painel institucional: so em tela larga. */}
      <aside className="hidden flex-col justify-between bg-topo p-12 text-sobre-topo lg:flex">
        <Marca tagline className="h-16" />
        <div className="max-w-md space-y-4">
          <p className="font-serif text-4xl leading-tight font-semibold">
            Avaliação morfofuncional do Mangalarga Marchador.
          </p>
          <p className="text-nevoa">
            Coleta padronizada de morfometria e dinâmica da marcha, validada ainda no haras, para um banco de
            dados que sustente o melhoramento da raça.
          </p>
        </div>
        <p className="text-xs tracking-wide text-nevoa/70 uppercase">Programa de avaliação · Protocolo v1.0</p>
      </aside>

      <main className="flex items-center justify-center p-6">
        <form onSubmit={entrar} className="w-full max-w-sm space-y-6">
          <header className="space-y-4">
            <div className="lg:hidden">
              <Marca tom="tema" />
            </div>
            <div className="space-y-1">
              <h1 className="font-serif text-3xl font-semibold text-marca">Entrar</h1>
              <p className="text-sm text-apagado">Use o e-mail e a senha fornecidos pela equipe Equiview.</p>
            </div>
          </header>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium">E-mail</span>
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={classeCampo}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium">Senha</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              className={classeCampo}
            />
          </label>

          {erro && (
            <p role="alert" className="border-l-2 border-erro bg-erro/8 px-3 py-2 text-sm text-erro">
              {erro}
            </p>
          )}

          <Botao type="submit" variante="principal" carregando={enviando} className="h-11 w-full">
            {enviando ? 'Entrando…' : 'Entrar'}
          </Botao>
          <button
            type="button"
            onClick={() => {
              setErro(null);
              setEnviado(false);
              setRecuperar(true);
            }}
            className="text-sm text-destaque-texto hover:underline"
          >
            Esqueci a senha
          </button>
        </form>
      </main>
    </div>
  );
}

/** Volta do link de "esqueci a senha": a senha nova, antes de entrar no app. */
export function NovaSenha({ aoTerminar }: { aoTerminar: () => void }) {
  const [senha, setSenha] = useState('');
  const [repeticao, setRepeticao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const salvar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (senha.length < 8) return setErro('A senha precisa de pelo menos 8 caracteres.');
    if (senha !== repeticao) return setErro('As duas senhas não são iguais.');
    setEnviando(true);
    setErro(null);
    const { error } = await supabase.auth.updateUser({ password: senha });
    setEnviando(false);
    if (error) setErro('Não foi possível trocar a senha. Peça um link novo e tente de novo.');
    else aoTerminar();
  };

  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <form onSubmit={salvar} className="w-full max-w-sm space-y-6">
        <header className="space-y-4">
          <Marca tom="tema" />
          <h1 className="font-serif text-3xl font-semibold text-marca">Senha nova</h1>
        </header>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Senha nova</span>
          <input type="password" required autoComplete="new-password" value={senha} onChange={(e) => setSenha(e.target.value)} className={classeCampo} />
        </label>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Repita a senha</span>
          <input type="password" required autoComplete="new-password" value={repeticao} onChange={(e) => setRepeticao(e.target.value)} className={classeCampo} />
        </label>
        {erro && (
          <p role="alert" className="border-l-2 border-erro bg-erro/8 px-3 py-2 text-sm text-erro">
            {erro}
          </p>
        )}
        <Botao type="submit" variante="principal" carregando={enviando} className="h-11 w-full">
          Salvar e entrar
        </Botao>
      </form>
    </main>
  );
}
